/**
 * Утилиты для оптимизации работы с аудио в Safari и мобильных браузерах
 */

export interface AudioConfig {
  src: string;
  volume?: number;
  loop?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
}

export class AudioManager {
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private audioContext: AudioContext | null = null;
  private userInteracted = false;
  private audioReady = false;

  constructor() {
    this.initializeAudioContext();
  }

  private initializeAudioContext() {
    try {
      // Создаем AudioContext только при необходимости
      if (typeof window !== 'undefined' && (window.AudioContext || (window as any).webkitAudioContext)) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    } catch (error) {
      console.warn('Не удалось создать AudioContext:', error);
    }
  }

  /**
   * Предзагружает аудио файл
   */
  async preloadAudio(key: string, config: AudioConfig): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const audio = new Audio();
        audio.src = config.src;
        audio.volume = config.volume ?? 0.5;
        audio.loop = config.loop ?? false;
        audio.preload = config.preload ?? 'auto';
        
        // Начинаем загрузку
        audio.load();

        const handleCanPlayThrough = () => {
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('error', handleError);
          this.audioElements.set(key, audio);
          resolve();
        };

        const handleError = (error: Event) => {
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('error', handleError);
          console.warn(`Ошибка загрузки аудио ${key}:`, error);
          reject(error);
        };

        audio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
        audio.addEventListener('error', handleError, { once: true });

        // Таймаут для загрузки
        setTimeout(() => {
          if (!this.audioElements.has(key)) {
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('error', handleError);
            console.warn(`Таймаут загрузки аудио ${key}`);
            reject(new Error('Audio load timeout'));
          }
        }, 10000); // 10 секунд таймаут

      } catch (error) {
        console.warn(`Ошибка при создании аудио элемента ${key}:`, error);
        reject(error);
      }
    });
  }

  /**
   * Воспроизводит предзагруженный звук
   */
  playAudio(key: string, options?: { volume?: number; resetTime?: boolean }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.userInteracted || !this.audioReady) {
        console.log('Аудио не готово или пользователь не взаимодействовал');
        resolve();
        return;
      }

      const audio = this.audioElements.get(key);
      if (!audio) {
        console.warn(`Аудио ${key} не найдено`);
        resolve();
        return;
      }

      try {
        if (options?.resetTime !== false) {
          audio.currentTime = 0;
        }
        
        if (options?.volume !== undefined) {
          audio.volume = options.volume;
        }

        audio.play()
          .then(() => {
            console.log(`Аудио ${key} успешно воспроизведено`);
            resolve();
          })
          .catch((error) => {
            console.warn(`Ошибка воспроизведения аудио ${key}:`, error);
            resolve(); // Не отклоняем промис, чтобы не ломать игру
          });
      } catch (error) {
        console.warn(`Ошибка при попытке воспроизвести аудио ${key}:`, error);
        resolve();
      }
    });
  }

  /**
   * Останавливает воспроизведение
   */
  stopAudio(key: string): void {
    const audio = this.audioElements.get(key);
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (error) {
        console.warn(`Ошибка при остановке аудио ${key}:`, error);
      }
    }
  }

  /**
   * Плавно изменяет громкость
   */
  fadeAudio(key: string, targetVolume: number, duration: number = 1000): void {
    const audio = this.audioElements.get(key);
    if (!audio) return;

    const startVolume = audio.volume;
    const volumeChange = targetVolume - startVolume;
    const steps = 20;
    const stepDuration = duration / steps;
    const volumeStep = volumeChange / steps;

    let currentStep = 0;
    const fadeInterval = setInterval(() => {
      currentStep++;
      audio.volume = startVolume + (volumeStep * currentStep);

      if (currentStep >= steps) {
        audio.volume = targetVolume;
        clearInterval(fadeInterval);
      }
    }, stepDuration);
  }

  /**
   * Отмечает, что пользователь взаимодействовал с приложением
   */
  setUserInteracted(): void {
    if (!this.userInteracted) {
      this.userInteracted = true;
      console.log('Пользователь взаимодействовал, аудио разблокировано');
      
      // Пробуем разблокировать AudioContext
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(console.warn);
      }
    }
  }

  /**
   * Устанавливает готовность аудио
   */
  setAudioReady(ready: boolean): void {
    this.audioReady = ready;
  }

  /**
   * Проверяет готовность аудио
   */
  isReady(): boolean {
    return this.userInteracted && this.audioReady;
  }

  /**
   * Очищает все аудио элементы
   */
  cleanup(): void {
    this.audioElements.forEach((audio) => {
      try {
        audio.pause();
        audio.src = '';
        audio.load();
      } catch (error) {
        console.warn('Ошибка при очистке аудио элемента:', error);
      }
    });
    this.audioElements.clear();
  }
}

/**
 * Создает глобальный экземпляр AudioManager
 */
export const audioManager = new AudioManager();

/**
 * Утилита для разблокировки аудио при первом взаимодействии пользователя
 */
export function setupAudioUnlock(): void {
  const events = ['click', 'touchstart', 'keydown', 'mousedown'];
  
  const handleUserInteraction = () => {
    audioManager.setUserInteracted();
    
    // Удаляем все обработчики после первого взаимодействия
    events.forEach(event => {
      document.removeEventListener(event, handleUserInteraction);
    });
  };

  events.forEach(event => {
    document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
  });
}

/**
 * Утилита для проверки поддержки аудио в браузере
 */
export function checkAudioSupport(): {
  audioContext: boolean;
  webAudio: boolean;
  autoplay: boolean;
} {
  const audioContext = !!(window.AudioContext || (window as any).webkitAudioContext);
  const webAudio = !!window.Audio;
  const autoplay = 'autoplay' in document.createElement('audio');
  
  return { audioContext, webAudio, autoplay };
}

/**
 * Конвертирует AudioBuffer в WAV Blob
 * Использует правильную симметричную формулу конвертации для сохранения качества и скорости воспроизведения
 * 
 * @param buffer - AudioBuffer для конвертации
 * @returns Promise с Blob в формате WAV
 */
export async function audioBufferToWav(buffer: AudioBuffer): Promise<Blob> {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length;
  
  // Создаем WAV заголовок
  const wavHeader = new ArrayBuffer(44);
  const view = new DataView(wavHeader);
  
  // RIFF заголовок
  view.setUint32(0, 0x52494646, false); // "RIFF"
  view.setUint32(4, 36 + length * numChannels * 2, true); // Размер файла
  view.setUint32(8, 0x57415645, false); // "WAVE"
  
  // fmt подзаголовок
  view.setUint32(12, 0x666D7420, false); // "fmt "
  view.setUint32(16, 16, true); // Размер fmt блока
  view.setUint16(20, 1, true); // PCM формат
  view.setUint16(22, numChannels, true); // Количество каналов
  view.setUint32(24, sampleRate, true); // Частота дискретизации
  view.setUint32(28, sampleRate * numChannels * 2, true); // Байт в секунду
  view.setUint16(32, numChannels * 2, true); // Байт на сэмпл
  view.setUint16(34, 16, true); // Бит на сэмпл
  
  // data подзаголовок
  view.setUint32(36, 0x64617461, false); // "data"
  view.setUint32(40, length * numChannels * 2, true); // Размер данных
  
  // Создаем массив для аудио данных
  const audioData = new Int16Array(length * numChannels);
  
  // Копируем данные из всех каналов в interleaved формате
  // Для стерео: sample[0][L], sample[0][R], sample[1][L], sample[1][R], ...
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      // Конвертируем float32 (-1.0 to 1.0) в int16 (-32768 to 32767)
      // Используем симметричную формулу для правильной конвертации
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      audioData[i * numChannels + channel] = Math.max(-32768, Math.min(32767, Math.round(sample * 32767)));
    }
  }
  
  // Создаем финальный blob
  const wavBlob = new Blob([wavHeader, audioData], { type: 'audio/wav' });
  return wavBlob;
}

